import { PageHero } from "@/components/PageHero";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Users, HandHeart, ShieldCheck, ArrowRight, ExternalLink, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, getSessionId } from "@/lib/api";

/**
 * /care-network — "People who can help."
 *
 * Two interest forms (private caregivers + local volunteers) at the top so
 * the founder gets demand signal from day 1. A curated list of national
 * free volunteer programs below so testers see real value today, before
 * the background-checked private-caregiver directory lights up.
 */

const CAREGIVER_NEEDS = [
    "Bathing & dressing",
    "Meal prep",
    "Companionship",
    "Medication reminders",
    "Toileting / incontinence",
    "Light housekeeping",
    "Transport to appointments",
    "Overnight care",
    "Respite (so I can step away)",
];

const VOLUNTEER_PROGRAMS = [
    {
        name: "Alzheimer's Association — Local volunteer & support programs",
        notes: "Free support groups, friendly callers, and care consultations. Every state has a local chapter that can point you to nearby volunteers.",
        website: "https://www.alz.org/help-support",
        phone: "1-800-272-3900",
    },
    {
        name: "Faith in Action",
        notes: "Interfaith volunteer caregiver networks — friendly visits, errands, transportation. Match is based on zip code and faith preference (or no preference).",
        website: "https://www.fianationalnetwork.org/",
    },
    {
        name: "Senior Companion Program (AmeriCorps Seniors)",
        notes: "Trained volunteer companions provide a few hours of friendly company each week so primary caregivers can step away. Free for eligible families.",
        website: "https://americorps.gov/serve/americorps-seniors/americorps-seniors-senior-companion-program",
    },
    {
        name: "Eldercare Locator (federal, all 50 states)",
        notes: "Call to be connected to your local Area Agency on Aging — they keep a current list of volunteer programs in your specific county.",
        website: "https://eldercare.acl.gov",
        phone: "1-800-677-1116",
    },
    {
        name: "VolunteerMatch — \"Dementia\" + your city",
        notes: "Searchable directory of vetted local volunteer opportunities. Filter by \"Seniors\" and your zip to find people already serving dementia families nearby.",
        website: "https://www.volunteermatch.org/",
    },
];

function RequestForm({ kind, title, icon: Icon, primaryColor, intro, ctaLabel, fields = ["zip", "hours", "needs", "notes"] }) {
    const sid = getSessionId();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [zip, setZip] = useState("");
    const [hours, setHours] = useState("");
    const [needs, setNeeds] = useState([]);
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");

    const toggleNeed = (n) => {
        setNeeds((s) => (s.includes(n) ? s.filter((x) => x !== n) : [...s, n]));
    };

    const submit = async (e) => {
        e.preventDefault();
        // Require at least one way to reach them.
        if (!email.trim() && !phone.trim()) {
            setErrorMsg("Please leave an email or phone so we can reach you.");
            return;
        }
        setErrorMsg("");
        setSubmitting(true);
        try {
            const r = await api.post("/care-network/request", {
                session_id: sid,
                kind,
                name,
                email,
                phone,
                zip,
                hours_needed: hours,
                needs,
                notes,
            });
            toast.success(r.data?.message || "Got it — we'll be in touch.");
            setDone(true);
        } catch {
            toast.error("Couldn't send. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <div className="ml-card p-6 md:p-7 bg-stage_early-light !border-stage_early-light text-center" data-testid={`care-${kind}-done`}>
                <CheckCircle2 size={36} strokeWidth={1.5} className="text-stage_early-dark mx-auto mb-2" />
                <h3 className="text-xl font-heading font-medium text-stage_early-dark">Thank you.</h3>
                <p className="mt-1.5 text-sm text-[#1F2624]">
                    {kind === "caregiver"
                        ? "We'll reach out as soon as a vetted match is in your area. In the meantime, scroll down for free volunteer programs that can help right now."
                        : "We'll connect you with local volunteer programs. Many of the ones listed below already serve your area — feel free to reach out directly."}
                </p>
            </div>
        );
    }

    return (
        <form
            onSubmit={submit}
            data-testid={`care-form-${kind}`}
            className="ml-card p-6 md:p-7 space-y-4"
        >
            <header className="flex items-start gap-3">
                <div className={`shrink-0 w-10 h-10 rounded-2xl ${primaryColor.bg} flex items-center justify-center`}>
                    <Icon strokeWidth={1.7} className={primaryColor.icon} />
                </div>
                <div className="flex-1">
                    <h3 className="text-xl font-heading font-medium text-[#1F2624]">{title}</h3>
                    <p className="mt-1 text-sm text-[#4C5551] leading-relaxed">{intro}</p>
                </div>
            </header>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <label htmlFor={`${kind}-name`} className="ml-label">Your name <span className="text-[#6E7A74]">(optional)</span></label>
                    <input id={`${kind}-name`} type="text" data-testid={`care-${kind}-name`}
                        value={name} onChange={(e) => setName(e.target.value)} className="ml-input" />
                </div>
                <div>
                    <label htmlFor={`${kind}-email`} className="ml-label">Email</label>
                    <input id={`${kind}-email`} type="email" data-testid={`care-${kind}-email`}
                        value={email} onChange={(e) => setEmail(e.target.value)} className="ml-input"
                        placeholder="you@example.com" />
                </div>
                <div>
                    <label htmlFor={`${kind}-phone`} className="ml-label">Phone <span className="text-[#6E7A74]">(optional)</span></label>
                    <input id={`${kind}-phone`} type="tel" data-testid={`care-${kind}-phone`}
                        value={phone} onChange={(e) => setPhone(e.target.value)} className="ml-input" />
                </div>
                {fields.includes("zip") && (
                    <div>
                        <label htmlFor={`${kind}-zip`} className="ml-label">Zip code</label>
                        <input id={`${kind}-zip`} type="text" inputMode="numeric" maxLength={10}
                            data-testid={`care-${kind}-zip`}
                            value={zip} onChange={(e) => setZip(e.target.value)} className="ml-input" placeholder="e.g. 75201" />
                    </div>
                )}
            </div>

            {fields.includes("hours") && (
                <div>
                    <label htmlFor={`${kind}-hours`} className="ml-label">When do you need help?</label>
                    <input id={`${kind}-hours`} type="text" data-testid={`care-${kind}-hours`}
                        value={hours} onChange={(e) => setHours(e.target.value)} className="ml-input"
                        placeholder="weekday mornings · overnight · 4 hours / day, weekends only" />
                </div>
            )}

            {fields.includes("needs") && (
                <div>
                    <p className="ml-label">What kind of help? <span className="text-[#6E7A74] font-normal">(pick all that apply)</span></p>
                    <div className="mt-2 flex flex-wrap gap-2">
                        {CAREGIVER_NEEDS.map((n) => {
                            const active = needs.includes(n);
                            return (
                                <button
                                    key={n}
                                    type="button"
                                    data-testid={`care-${kind}-need-${n.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                    onClick={() => toggleNeed(n)}
                                    className={`px-3.5 py-1.5 rounded-2xl text-sm font-medium border transition-colors ${active
                                        ? `${primaryColor.bg} ${primaryColor.icon} border-transparent`
                                        : "bg-white text-[#4C5551] border-[#E2E8F0] hover:bg-cream-dark"
                                        }`}
                                >
                                    {n}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            <div>
                <label htmlFor={`${kind}-notes`} className="ml-label">Anything else we should know? <span className="text-[#6E7A74]">(optional)</span></label>
                <textarea id={`${kind}-notes`} rows={3} data-testid={`care-${kind}-notes`}
                    value={notes} onChange={(e) => setNotes(e.target.value)} className="ml-input"
                    placeholder={kind === "caregiver"
                        ? "Stage of dementia, behaviors, language preferences, anything else."
                        : "What would be most meaningful? A weekly call, a few hours of company, errands?"} />
            </div>

            {errorMsg && (
                <p className="text-sm text-coral-dark" data-testid={`care-${kind}-error`}>{errorMsg}</p>
            )}

            <button
                type="submit"
                disabled={submitting}
                data-testid={`care-${kind}-submit`}
                className="ml-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {submitting ? "Sending…" : ctaLabel}
                <ArrowRight size={16} strokeWidth={1.8} />
            </button>
        </form>
    );
}

export default function CareNetwork() {
    return (
        <div className="space-y-10 animate-fade-in-up" data-testid="care-network-page">
            <PageHero
                testId="care-network-header"
                kicker="Community support"
                kickerIcon={Users}
                title="People who can help"
                subtitle={<>You don&apos;t have to carry this alone. Tell us what you need — a paid private caregiver or a local volunteer — and we&apos;ll point you to the right people.</>}
            >
                <p className="mt-4 text-sm text-[#4C5551] max-w-2xl bg-yellow-soft/40 border border-yellow-soft rounded-2xl px-4 py-3 flex items-start gap-2">
                    <ShieldCheck size={18} strokeWidth={1.7} className="text-yellow-dark shrink-0 mt-0.5" />
                    <span>
                        <strong>Coming soon:</strong> background-checked private caregivers in your area, vetted
                        by Memory Lane. For now we&apos;ll personally email or call you with the best match we can find.
                    </span>
                </p>
            </PageHero>

            <section className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <RequestForm
                    kind="caregiver"
                    title="Request a private caregiver"
                    icon={ShieldCheck}
                    primaryColor={{ bg: "bg-teal-soft", icon: "text-teal-dark" }}
                    intro="Paid, hands-on help in your home. We'll match you to a background-checked caregiver once we open in your area."
                    ctaLabel="Send my request"
                />
                <RequestForm
                    kind="volunteer"
                    title="Find local volunteers"
                    icon={HandHeart}
                    primaryColor={{ bg: "bg-stage_late-light", icon: "text-stage_late-dark" }}
                    intro="Free help — a friendly visit, an errand, an hour of company so you can step away. We'll point you to verified programs near you."
                    ctaLabel="Send my request"
                    fields={["zip", "hours", "notes"]}
                />
            </section>

            <section data-testid="care-network-programs" className="space-y-4">
                <div>
                    <h2 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight">Free volunteer programs you can call right now</h2>
                    <p className="mt-2 text-sm text-[#4C5551] max-w-2xl">
                        Vetted national networks. Most have a local chapter or zip-code matcher.
                    </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {VOLUNTEER_PROGRAMS.map((p, i) => (
                        <article
                            key={p.name}
                            data-testid={`care-program-${i}`}
                            className="ml-card ml-card-hover p-6"
                        >
                            <p className="text-xs uppercase tracking-wider font-semibold text-teal">National</p>
                            <h3 className="mt-1 text-lg font-heading font-medium text-[#1F2624]">{p.name}</h3>
                            <p className="mt-2 text-sm text-[#4C5551] leading-relaxed">{p.notes}</p>
                            <div className="mt-4 flex flex-wrap gap-2">
                                {p.phone && (
                                    <a
                                        href={`tel:${p.phone.replace(/\D/g, "")}`}
                                        className="ml-btn-primary !min-h-[44px] !py-2 !px-4 text-sm"
                                    >
                                        {p.phone}
                                    </a>
                                )}
                                {p.website && (
                                    <a
                                        href={p.website}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="ml-btn-coral !min-h-[44px] !py-2 !px-4 text-sm"
                                    >
                                        <ExternalLink size={16} strokeWidth={1.6} /> Visit
                                    </a>
                                )}
                            </div>
                        </article>
                    ))}
                </div>
            </section>

            <section className="ml-card p-6 md:p-7 bg-cream-dark/40 !border-cream-dark text-center">
                <p className="text-sm text-[#4C5551] max-w-xl mx-auto">
                    Looking for something else? <Link to="/help-finder" className="text-teal-dark underline underline-offset-2">Browse Resources</Link> for state-by-state programs (financial help, respite, legal, hospice).
                </p>
            </section>
        </div>
    );
}
